import { useId, useLayoutEffect } from 'react';

export function messageMenuPosition(anchor, menu, viewport, rtl = false) {
  const margin = 8, gap = 6;
  const leftEdge = (viewport.left || 0) + margin;
  const topEdge = (viewport.top || 0) + margin;
  const rightEdge = (viewport.left || 0) + viewport.width - margin;
  const bottomEdge = (viewport.top || 0) + viewport.height - margin;
  const below = Math.max(0, bottomEdge - anchor.bottom - gap);
  const above = Math.max(0, anchor.top - topEdge - gap);
  const opensBelow = below >= menu.height || below >= above;
  const maxHeight = Math.max(0, opensBelow ? below : above);
  const height = Math.min(menu.height, maxHeight);
  const maxWidth = Math.max(0, viewport.width - margin * 2);
  const width = Math.min(menu.width, maxWidth);
  const preferredLeft = rtl ? anchor.right - width : anchor.left;
  return {
    left: Math.max(leftEdge, Math.min(preferredLeft, rightEdge - width)),
    top: opensBelow ? anchor.bottom + gap : anchor.top - gap - height,
    maxHeight,
    maxWidth,
  };
}

export default function useAnchoredMessageMenu(open, triggerRef, menuRef, onClose) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const className = `talk-menu-position-${id}`;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) return;
    // Dynamic coordinates belong to a scoped CSS class, not inline attributes.
    const stylesheet = document.createElement('style');
    stylesheet.setAttribute('data-talk-menu-position', id);
    document.head.appendChild(stylesheet);
    const update = () => {
      const trigger = triggerRef.current, menu = menuRef.current;
      if (!trigger || !menu) return;
      const anchor = trigger.getBoundingClientRect();
      const visual = window.visualViewport;
      const viewport = { left: visual?.offsetLeft || 0, top: visual?.offsetTop || 0,
        width: visual?.width || window.innerWidth, height: visual?.height || window.innerHeight };
      const scroller = trigger.closest('.talk-chat-messages')?.getBoundingClientRect();
      if (anchor.height > 0 && (anchor.bottom <= viewport.top || anchor.top >= viewport.top + viewport.height ||
          (scroller?.height > 0 && (anchor.bottom <= scroller.top || anchor.top >= scroller.bottom)))) {
        onClose();
        return;
      }
      const bounds = menu.getBoundingClientRect();
      const position = messageMenuPosition(anchor, {
        width: bounds.width, height: Math.max(bounds.height, menu.scrollHeight + 2),
      }, viewport, getComputedStyle(trigger).direction === 'rtl');
      const rule = `.${className}{left:${position.left}px;top:${position.top}px;max-height:${position.maxHeight}px;max-width:${position.maxWidth}px;}`;
      if (stylesheet.textContent !== rule) stylesheet.textContent = rule;
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(menuRef.current);
    window.addEventListener('resize', update);
    document.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      document.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      stylesheet.remove();
    };
  }, [open, className, id, triggerRef, menuRef, onClose]);

  return className;
}
